/**
 * QuotaMeter — used / limit progress bar for a single usage metric.
 * Accepts either a normalized quota (see parseQuotaError.js) or the raw payload.
 */

import { h } from 'preact'
import { normalizeQuota } from './parseQuotaError.js'
import { t as defaultT, metricLabel } from './quota-i18n.js'

const BYTE_METRIC = /storage|bytes/i

export function quotaVariant(ratio) {
  if (ratio == null) return 'primary'
  if (ratio >= 1) return 'danger'
  if (ratio >= 0.9) return 'warning'
  if (ratio >= 0.75) return 'info'
  return 'success'
}

function formatBytes(bytes) {
  const n = Number(bytes)
  if (!Number.isFinite(n)) return '—'
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`
  return `${(n / (1024 * 1024 * 1024)).toFixed(1)} GB`
}

/** `format` is 'bytes' | 'number' | undefined (derived from the metric name). */
export function formatQuotaValue(value, { metric, format } = {}) {
  if (value == null) return '—'
  const asBytes = format === 'bytes' || (format == null && BYTE_METRIC.test(String(metric ?? '')))
  if (asBytes) return formatBytes(value)
  const n = Number(value)
  return Number.isFinite(n) ? n.toLocaleString() : '—'
}

export function QuotaMeter({
  quota,
  t = defaultT,
  label,
  format,
  compact = false,
  showValues = true,
  className = ''
}) {
  const data = normalizeQuota(quota)
  if (!data) return null

  const { metric, used, limit, ratio } = data
  const percent = ratio == null ? 0 : Math.min(Math.round(ratio * 100), 100)
  const variant = quotaVariant(ratio)
  const title = label ?? metricLabel(metric, t)
  const usedText = formatQuotaValue(used, { metric, format })
  const limitText = limit == null
    ? t('quotas.unlimited')
    : formatQuotaValue(limit, { metric, format })

  const classes = ['quota-meter', compact ? 'quota-meter-compact' : null, className]
    .filter(Boolean).join(' ')

  return (
    <div class={classes}>
      {(title || showValues) && (
        <div class="d-flex justify-content-between align-items-baseline gap-2 small">
          {title && <span class="text-truncate">{title}</span>}
          {showValues && (
            <span class={`text-nowrap ${ratio != null && ratio >= 1 ? 'text-danger fw-semibold' : 'text-muted'}`}>
              {t('quotas.used_of_limit', { used: usedText, limit: limitText })}
            </span>
          )}
        </div>
      )}
      <div
        class={`progress ${compact ? 'quota-meter-bar-compact' : ''}`}
        style={compact ? 'height: 4px' : 'height: 8px'}
        role="progressbar"
        aria-label={title || metric}
        aria-valuenow={percent}
        aria-valuemin="0"
        aria-valuemax="100"
      >
        <div class={`progress-bar bg-${variant}`} style={`width: ${percent}%`} />
      </div>
    </div>
  )
}

export default QuotaMeter
